import Button from 'components/common/Button';
import ScrollToError from 'components/common/ScrollToError';
import { Field, Form, Formik, type FormikHelpers } from 'formik';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import NumberInput from 'components/inputs/NumberInput';
import * as yup from 'yup';
import { grabErrorMessage } from 'utils/helpers';
import { numberTypeError, translatedMessage } from 'utils/yup';
import { CAlert, CCard, CCardBody, CCardHeader, CCol, CRow } from '@coreui/react';
import useAdjustExpressLoanSettings from 'hooks/expressLoan/useAdjustExporessLoanSettings';
import useExpressLoanSettings from 'hooks/expressLoan/useExpressLoanSettings';
import Loading from 'components/common/Loading';
import { toast } from 'react-toastify';
import type { TExpressLoanSettings } from 'hooks/expressLoan/useExpressLoanSettings';

// The former `.nullable(message)` after required() took the message as a flag and still let required() reject null
// with its message, which required() alone does in yup 1 (a nullable() after it would let null through). An empty
// field is NaN, which fails with the type error.
const validationSchema = yup.object().shape({
  total_account_fees: yup
    .number()
    .required(translatedMessage('errors.fill_field'))
    .typeError(numberTypeError),
  execution_fee: yup
    .number()
    .required(translatedMessage('errors.fill_field'))
    .typeError(numberTypeError),
});

interface IExpressLoanSettingsValues {
  total_account_fees: number;
  execution_fee: number;
}

interface IExpressLoanSettingsProps {
  expressLoanSettings: TExpressLoanSettings | null;
}

const ExpressLoanSettings = ({ expressLoanSettings }: IExpressLoanSettingsProps) => {
  const { formatMessage: t } = useIntl();
  const adjustExpressLoanSettings = useAdjustExpressLoanSettings();

  const onSubmit = useCallback(
    async (
      values: IExpressLoanSettingsValues,
      helpers: FormikHelpers<IExpressLoanSettingsValues>,
    ) => {
      // Bug: useAdjustExpressLoanSettings catches a failed save (and toasts its error), so the success toast
      // follows the error toast; kept as it was.
      await adjustExpressLoanSettings(values);
      toast.success(t({ id: 'expressLoan.changes_applied' }));
      helpers.setSubmitting(false);
    },
    [adjustExpressLoanSettings, t],
  );

  const initialValues: IExpressLoanSettingsValues = {
    total_account_fees: expressLoanSettings?.total_account_fees || 0,
    execution_fee: expressLoanSettings?.execution_fee || 0,
  };

  return (
    <CRow>
      <CCol xs={12} lg={12}>
        <CCard>
          <CCardHeader>{t({ id: 'page_titles.financing_requests' })}</CCardHeader>
          <CCardBody>
            <Formik {...{ initialValues, onSubmit, validationSchema }}>
              {({ isSubmitting }) => (
                <Form>
                  <ScrollToError />
                  <Field
                    name="total_account_fees"
                    component={NumberInput}
                    required
                    label={t({ id: 'expressLoan.total_account_fees' })}
                  />
                  <Field
                    name="execution_fee"
                    component={NumberInput}
                    required
                    label={t({ id: 'expressLoan.execution_fee' })}
                  />

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                      {t({ id: 'buttons.save' })}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

export default () => {
  const { expressLoanSettings, loading, error } = useExpressLoanSettings();

  if (loading) return <Loading size={50} />;

  if (error) return <CAlert color="danger">{grabErrorMessage(error)}</CAlert>;

  return <ExpressLoanSettings {...{ expressLoanSettings }} />;
};
